import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Briefcase, 
  ClipboardList, 
  Users, 
  Settings, 
  LogOut,
  Menu,
  X,
  Building2,
  DollarSign,
  FileText,
  BarChart3,
  ShoppingCart,
  Upload,
  Receipt,
  History,
  HardHat,
  FileEdit,
  Scale,
  ShieldCheck,
  ArrowLeftRight,
  FileSpreadsheet
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// No default logo - companies should upload their own
const DEFAULT_LOGO = null;

export default function Layout({ children }) {
  const { user, logout, token } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companyName, setCompanyName] = useState('BSO SiteTrack');
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    const fetchCompany = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/company`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.name) setCompanyName(res.data.name);
        // Set logo URL - could be base64 data URL or actual URL
        // Empty string or null means no logo
        setLogoUrl(res.data.logoUrl || null);
      } catch (error) {
        // Use defaults if company not found
        console.log('Using default company branding');
      }
    };
    fetchCompany();
  }, [token]);

  const getNavItems = () => {
    const baseItems = [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['FOREMAN', 'OFFICE', 'OWNER'] },
      { path: '/dashboard/recap', label: 'Job Recap', icon: BarChart3, roles: ['FOREMAN', 'OFFICE', 'OWNER'] },
      { path: '/jobs', label: 'Jobs', icon: Briefcase, roles: ['FOREMAN', 'OFFICE', 'OWNER'] },
    ];

    const foremanItems = [
      { path: '/material-pos', label: 'Material POs', icon: ShoppingCart, roles: ['FOREMAN'] },
      { path: '/timesheets', label: 'Timesheets', icon: ClipboardList, roles: ['FOREMAN'] },
    ];

    const officeItems = [
      { path: '/customers', label: 'Customers', icon: Building2, roles: ['OFFICE', 'OWNER'] },
      { path: '/material-pos', label: 'Material POs', icon: ShoppingCart, roles: ['OFFICE', 'OWNER'] },
      { path: '/change-orders', label: 'Change Orders', icon: FileEdit, roles: ['OFFICE', 'OWNER'] },
      { path: '/cross-charges', label: 'Cross-Charges', icon: ArrowLeftRight, roles: ['OFFICE', 'OWNER'] },
      { path: '/timesheets', label: 'Timesheets', icon: ClipboardList, roles: ['OFFICE', 'OWNER'] },
      { path: '/invoices', label: 'Vendor Invoices', icon: FileText, roles: ['OFFICE', 'OWNER'] },
      { path: '/customer-invoices', label: 'Customer Invoices', icon: Receipt, roles: ['OFFICE', 'OWNER'] },
      { path: '/crew', label: 'Crew', icon: Users, roles: ['OFFICE', 'OWNER'] },
      { path: '/rates', label: 'Rates', icon: DollarSign, roles: ['OFFICE', 'OWNER'] },
      { path: '/client-rates', label: 'Client Rates', icon: FileSpreadsheet, roles: ['OFFICE', 'OWNER'] },
      { path: '/purchase-orders', label: 'Purchase Orders', icon: DollarSign, roles: ['OFFICE', 'OWNER'] },
      { path: '/import', label: 'Import Data', icon: Upload, roles: ['OFFICE', 'OWNER'] },
      { path: '/audit-log', label: 'Audit Log', icon: History, roles: ['OFFICE', 'OWNER'] },
      { path: '/settings', label: 'Settings', icon: Settings, roles: ['OFFICE', 'OWNER'] },
      { path: '/terms', label: 'Terms & Legal', icon: Scale, roles: ['OFFICE', 'OWNER', 'FOREMAN'] },
    ];

    // Super Admin items
    const superAdminItems = [
      { path: '/admin', label: 'Admin Dashboard', icon: ShieldCheck, roles: ['SUPER_ADMIN'] },
    ];

    let items = [...baseItems];
    
    // Super Admin gets everything plus admin dashboard
    if (user?.role === 'SUPER_ADMIN') {
      return [...superAdminItems, ...items, ...officeItems];
    } else if (user?.role === 'FOREMAN') {
      items = [...items, ...foremanItems];
    } else if (user?.role === 'OFFICE' || user?.role === 'OWNER') {
      items = [...items, ...officeItems];
    }

    return items.filter(item => item.roles.includes(user?.role));
  };

  const navItems = getNavItems();

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-64 bg-[#1A3668] text-white">
        <div className="p-6 border-b border-white border-opacity-10">
          <div className="h-14 mb-2 flex items-center">
            {logoUrl ? (
              <div className="bg-white rounded-sm p-2 h-14 flex items-center justify-center">
                <img 
                  src={logoUrl} 
                  alt={companyName}
                  className="h-10 max-w-[180px] object-contain"
                  onError={(e) => {
                    e.target.parentElement.style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <HardHat className="w-8 h-8" />
                <span className="font-bold text-lg">{companyName}</span>
              </div>
            )}
          </div>
          <p className="text-xs uppercase tracking-wider text-white text-opacity-70">{companyName}</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-white bg-opacity-20 text-white'
                    : 'text-white text-opacity-80 hover:bg-white hover:bg-opacity-10'
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white border-opacity-10">
          <div className="mb-3 px-4">
            <p className="text-sm font-bold text-white">{user?.name}</p>
            <p className="text-xs text-white text-opacity-70">{user?.email}</p>
            <p className="text-xs uppercase tracking-wider text-white text-opacity-50 mt-1">{user?.role}</p>
          </div>
          <Button
            onClick={logout}
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start text-white hover:bg-white hover:bg-opacity-10 rounded-sm"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
        
        {/* Footer tagline */}
        <div className="p-3 text-center border-t border-white border-opacity-10">
          <p className="text-[10px] text-white text-opacity-40">
            Created by BSO Financial Services
          </p>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-[#1A3668] text-white z-50 shadow-lg">
        <div className="flex items-center justify-between p-4">
          <div className="h-10 flex items-center">
            {logoUrl ? (
              <div className="bg-white rounded-sm p-1 h-10 flex items-center justify-center">
                <img 
                  src={logoUrl} 
                  alt={companyName}
                  className="h-8 max-w-[120px] object-contain"
                />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <HardHat className="w-6 h-6" />
                <span className="font-bold">{companyName}</span>
              </div>
            )}
          </div>
          <Button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            variant="ghost"
            size="icon"
            data-testid="mobile-menu-toggle"
            className="text-white hover:bg-white hover:bg-opacity-10 h-12 w-12"
          >
            {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </Button>
        </div>

        {/* Mobile Menu - Full Screen Overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 top-[72px] bg-[#1A3668] z-[10000] flex flex-col">
            <nav className="flex-1 overflow-y-auto p-4 space-y-2">
              {navItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className={`flex items-center gap-4 px-4 py-4 rounded-sm font-medium transition-colors text-lg ${
                      isActive(item.path)
                        ? 'bg-white bg-opacity-20 text-white'
                        : 'text-white text-opacity-80 hover:bg-white hover:bg-opacity-10 active:bg-white active:bg-opacity-20'
                    }`}
                  >
                    <Icon className="w-6 h-6" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            
            {/* User Info and Logout - fixed at bottom */}
            <div className="flex-shrink-0 p-4 pb-16 border-t border-white border-opacity-10 bg-[#142A52]">
              <div className="mb-4 px-2">
                <p className="text-base font-bold text-white">{user?.name}</p>
                <p className="text-sm text-white text-opacity-70">{user?.email}</p>
                <p className="text-xs uppercase tracking-wider text-white text-opacity-50 mt-1">{user?.role}</p>
              </div>
              <Button
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
                data-testid="mobile-logout-button"
                variant="ghost"
                className="w-full justify-start text-white hover:bg-white hover:bg-opacity-10 active:bg-white active:bg-opacity-20 rounded-sm py-4 text-lg"
              >
                <LogOut className="w-6 h-6 mr-3" />
                Logout
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 md:overflow-auto mt-[72px] md:mt-0 overflow-x-hidden">
        <div className="min-h-screen bg-[#F8F9FA]">
          {children}
        </div>
      </main>
    </div>
  );
}
